import {
  APPLICATION_HISTORY_COLUMNS,
  APPLICATION_HISTORY_DEFAULT_VISIBLE,
} from '../../../constants/fundingApplicationOptions';

export const COLUMN_STORAGE_KEY = 'fundgenius.applicationHistory.columns.v1';
const LEGACY_STORAGE_KEY = 'funding-application-visible-columns';

const LOCKED_VISIBLE_IDS = new Set([
  'dateOfApplication',
  'entity',
  'applicants',
  'lender',
  'actions',
]);

export function getColumnMeta(id) {
  const base = APPLICATION_HISTORY_COLUMNS.find((col) => col.id === id);
  if (!base) return null;
  return {
    id: base.id,
    label: base.label,
    category: base.category,
    sensitive: Boolean(base.sensitive),
    lockedVisible: base.lockedVisible ?? (LOCKED_VISIBLE_IDS.has(base.id) || Boolean(base.locked)),
    reorderable: base.reorderable !== false,
  };
}

export function buildDefaultColumnConfig() {
  const defaultVisible = new Set(APPLICATION_HISTORY_DEFAULT_VISIBLE);
  return APPLICATION_HISTORY_COLUMNS.map((col) => ({
    id: col.id,
    visible: defaultVisible.has(col.id),
  }));
}

export function mergeColumnConfig(saved) {
  const defaultConfig = buildDefaultColumnConfig();
  if (!saved || !Array.isArray(saved) || saved.length === 0) {
    return defaultConfig;
  }

  const knownIds = new Set(APPLICATION_HISTORY_COLUMNS.map((col) => col.id));
  const savedEntries = saved
    .map((item) => {
      if (typeof item === 'string') return { id: item, visible: true };
      if (item && typeof item.id === 'string') {
        return { id: item.id, visible: item.visible !== false };
      }
      return null;
    })
    .filter((item) => item && knownIds.has(item.id));

  const savedVisibleById = new Map(savedEntries.map((item) => [item.id, item.visible]));
  const orderedIds = [];
  savedEntries.forEach((item) => {
    if (!orderedIds.includes(item.id)) orderedIds.push(item.id);
  });
  defaultConfig.forEach((item) => {
    if (!orderedIds.includes(item.id)) orderedIds.push(item.id);
  });

  return orderedIds.map((id) => {
    const meta = getColumnMeta(id);
    const defaultItem = defaultConfig.find((col) => col.id === id);
    const savedVisible = savedVisibleById.get(id);
    const visible = meta?.lockedVisible
      ? true
      : (savedVisible !== undefined ? savedVisible : (defaultItem?.visible ?? false));
    return { id, visible };
  });
}

export function loadApplicationHistoryColumns() {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return mergeColumnConfig(parsed);
    }
  } catch {
    // ignore
  }

  try {
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      if (Array.isArray(legacy) && legacy.length) {
        const migrated = mergeColumnConfig(
          legacy.map((id) => ({ id, visible: true })),
        );
        saveApplicationHistoryColumns(migrated);
        return migrated;
      }
    }
  } catch {
    // ignore
  }

  return buildDefaultColumnConfig();
}

export function saveApplicationHistoryColumns(config) {
  try {
    localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function getOrderedVisibleColumnIds(config = []) {
  return config.filter((col) => col.visible).map((col) => col.id);
}

export function resolveOrderedVisibleColumns(config = []) {
  const byId = new Map(APPLICATION_HISTORY_COLUMNS.map((col) => [col.id, col]));
  return config
    .filter((col) => col.visible)
    .map((col) => byId.get(col.id))
    .filter(Boolean);
}

export function insertColumnBeforeActions(config, columnId) {
  const next = config.map((col) => ({ ...col }));
  const fromIndex = next.findIndex((col) => col.id === columnId);
  if (fromIndex < 0) return next;

  const actionsIndex = next.findIndex((col) => col.id === 'actions');
  const [item] = next.splice(fromIndex, 1);
  item.visible = true;

  if (actionsIndex >= 0) {
    const insertAt = fromIndex < actionsIndex ? actionsIndex - 1 : actionsIndex;
    next.splice(insertAt, 0, item);
  } else {
    next.push(item);
  }

  return next;
}

/** @deprecated Use loadApplicationHistoryColumns + getOrderedVisibleColumnIds */
export function loadVisibleColumns() {
  return getOrderedVisibleColumnIds(loadApplicationHistoryColumns());
}

/** @deprecated Use saveApplicationHistoryColumns */
export function saveVisibleColumns(columns) {
  if (!Array.isArray(columns)) return;
  const config = mergeColumnConfig(columns.map((id) => ({ id, visible: true })));
  saveApplicationHistoryColumns(config);
}
